import Tabs from 'antd/lib/tabs';
import React, { useMemo, useState } from 'react';
import { CollapseFilterWrapper } from 'src/components/sidebar-filters/CollapseFilterWrapper';
import { SidebarFilterActions, TSidebarActions } from 'src/components/sidebar-filters/SidebarFilterActions';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import { SavedFiltersList } from 'src/components/sidebar-filters/SavedFiltersList';
import {
    ICreateSavedFilter,
    ISavedFilter,
    SavedFilterStatus,
    SavedFilterSubType,
    SavedFilterType,
} from 'src/components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from 'src/components/sidebar-filters/useSavedFiltersList';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from 'src/domain/saved-filters/queries';
import { useCustomizableTableContext } from 'src/components/table/CustomizableTableContext';
import { CUSTOMER_UTILITY_BILL_SIDEBAR } from './consts';
import { IBillSidebarFilters, countActiveBillFilters, emptyBillSidebarFilters } from './billSidebarFilters';
import { cleanBillFilterValues } from './billSavedFilters';
import { SaveCustomerUtilityBillFiltersModal } from './SaveCustomerUtilityBillFiltersModal';
import { BillPeriodFilterSelect } from '../../sidebar-filters/filter-select/BillPeriodFilterSelect';
import { BillStatusFilterSelect } from '../../sidebar-filters/filter-select/BillStatusFilterSelect';
import { BillVerificationFilterSelect } from '../../sidebar-filters/filter-select/BillVerificationFilterSelect';
import { BillUtilityFilterSelect } from '../../sidebar-filters/filter-select/BillUtilityFilterSelect';

type TSidebarTab = 'filters' | 'grid' | 'saved';

type TBillTabItem = {
    key: TSidebarTab;
    label: React.ReactNode;
    children: React.ReactNode;
};

interface ICustomerUtilityBillSidebarTabsProps {
    filters: IBillSidebarFilters;
    onChange: (filters: IBillSidebarFilters) => void;
    onClearFilters: () => void;
    utilityOptions: { value: string; label: string }[];
}

const buildNewSavedFilter = (filters: IBillSidebarFilters): ICreateSavedFilter => ({
    id: null,
    filter_name: '',
    description: '',
    filter_values: cleanBillFilterValues(filters),
    status: SavedFilterStatus.active,
    type: SavedFilterType.filter,
    sub_type: SavedFilterSubType.customerUtilityBills,
    display_in_dashboard: false,
});

const savedFilterToEditable = (filter: ISavedFilter): ICreateSavedFilter => ({
    id: filter.id,
    filter_name: filter.filter_name,
    description: filter.description,
    filter_values: filter.filter_values,
    status: filter.status,
    type: filter.type,
    sub_type: filter.sub_type,
    display_in_dashboard: filter.display_in_dashboard,
});

export const CustomerUtilityBillSidebarTabs: React.FC<ICustomerUtilityBillSidebarTabsProps> = ({
    filters,
    onChange,
    onClearFilters,
    utilityOptions,
}) => {
    const [activeTab, setActiveTab] = useState<TSidebarTab>('filters');
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);
    const [editingSavedFilter, setEditingSavedFilter] = useState<ICreateSavedFilter | null>(null);

    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.customerUtilityBills);
    const { mutateAsync: createSavedFilter, isLoading: isCreating } = useSavedFilterCreateMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdating } = useSavedFilterUpdateMutation();
    const { mutateAsync: deleteSavedFilter } = useSavedFilterDeleteMutation();

    const handleTabChange = (key: string) => {
        setActiveTab(key as TSidebarTab);
    };

    const handleChange = <K extends keyof IBillSidebarFilters>(fieldName: K, value: IBillSidebarFilters[K]) => {
        // A manual edit means the result no longer matches the applied saved filter.
        setAppliedSavedFilter(null);
        onChange({
            ...filters,
            [fieldName]: value,
        });
    };

    const handleApplySavedFilter = (filter: ISavedFilter) => {
        setAppliedSavedFilter(filter);
        onChange({ ...emptyBillSidebarFilters, ...(filter.filter_values as Partial<IBillSidebarFilters>) });
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        await deleteSavedFilter(filter.id);
        if (appliedSavedFilter?.id === filter.id) setAppliedSavedFilter(null);
    };

    const handleOpenSaveModal = () => setEditingSavedFilter(buildNewSavedFilter(filters));
    const handleOpenEditModal = (filter: ISavedFilter) => setEditingSavedFilter(savedFilterToEditable(filter));
    const handleCloseSaveModal = () => setEditingSavedFilter(null);

    const handleSaveFilter = async (filterData: ICreateSavedFilter) => {
        if (filterData.id) {
            await updateSavedFilter(filterData);
        } else {
            await createSavedFilter(filterData);
        }

        setEditingSavedFilter(null);
    };

    const handleClearSavedFilters = () => {
        setAppliedSavedFilter(null);
        onClearFilters();
    };

    const activeCount = countActiveBillFilters(filters);

    const tabs: TBillTabItem[] = [
        {
            key: 'filters',
            label: <span>Filters{activeCount > 0 ? ` (${activeCount})` : ''}</span>,
            children: (
                <TabContentWrapper
                    activeTab={activeTab}
                    isSaveDisabled={activeCount === 0}
                    onSaveFilters={handleOpenSaveModal}
                    onClearFilters={onClearFilters}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
                        <CollapseFilterWrapper label="Filters">
                            <BillPeriodFilterSelect value={filters.period} onChange={v => handleChange('period', v)} />

                            <BillStatusFilterSelect value={filters.statuses} onChange={v => handleChange('statuses', v)} />

                            <BillVerificationFilterSelect value={filters.verification} onChange={v => handleChange('verification', v)} />

                            <BillUtilityFilterSelect
                                value={filters.utilities}
                                onChange={v => handleChange('utilities', v)}
                                options={utilityOptions}
                            />
                        </CollapseFilterWrapper>
                    </div>
                </TabContentWrapper>
            ),
        },
        {
            key: 'grid',
            label: <GridLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab}>
                    <div style={{ padding: '8px 4px' }}>
                        <SidebarGrid />
                    </div>
                </TabContentWrapper>
            ),
        },
        {
            key: 'saved',
            label: <span>Saved{savedFilters.length > 0 ? ` (${savedFilters.length})` : ''}</span>,
            children: (
                <TabContentWrapper activeTab={activeTab} onClearFilters={handleClearSavedFilters}>
                    <SavedFiltersList
                        savedFilters={savedFilters}
                        appliedSavedFilter={appliedSavedFilter}
                        onApplySavedFilter={handleApplySavedFilter}
                        onDeleteSavedFilter={handleDeleteSavedFilter}
                        onOpenSaveFiltersModal={handleOpenEditModal}
                    />
                </TabContentWrapper>
            ),
        },
    ];

    return (
        <>
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                defaultActiveKey="filters"
                destroyOnHidden={false}
                items={tabs}
                tabBarStyle={{ marginBottom: 0, padding: '0px 5px' }}
            />

            {editingSavedFilter && (
                <SaveCustomerUtilityBillFiltersModal
                    isLoading={isCreating || isUpdating}
                    initialSavedFilter={editingSavedFilter}
                    onCancel={handleCloseSaveModal}
                    onSave={handleSaveFilter}
                />
            )}
        </>
    );
};

const GridLabel: React.FC = () => {
    const { hiddenColumnsCount } = useCustomizableTableContext();

    return <span>Grid {hiddenColumnsCount > 0 ? `(${hiddenColumnsCount})` : ''}</span>;
};

interface ITabContentWrapperProps {
    children: React.ReactNode;
    activeTab: TSidebarTab;
    isSaveDisabled?: boolean;
    onSaveFilters?: () => void;
    onClearFilters?: () => void;
}

const TabContentWrapper: React.FC<ITabContentWrapperProps> = ({ children, activeTab, isSaveDisabled, onSaveFilters, onClearFilters }) => {
    const { resetColumns } = useCustomizableTableContext();

    const shownActions: TSidebarActions[] = useMemo(() => {
        if (activeTab === 'filters') return ['saveFilters', 'clearFilters'];
        if (activeTab === 'saved') return ['clearFilters'];
        if (activeTab === 'grid') return ['resetGrid'];

        return [];
    }, [activeTab]);

    return (
        <div style={{ height: CUSTOMER_UTILITY_BILL_SIDEBAR.tabContentHeight, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>{children}</div>
            <SidebarFilterActions
                shownActions={shownActions}
                isSaveDisabled={isSaveDisabled}
                onSaveFilters={onSaveFilters ?? (() => undefined)}
                onClearFilters={onClearFilters ?? (() => undefined)}
                onResetGrid={resetColumns}
            />
        </div>
    );
};
